import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  loadAiSummaries,
  loadAiSummariesError,
  loadAiSummariesSuccess,
  loadCandidates,
  loadCandidatesError,
  loadCandidatesSuccess,
  setStatusId,
} from './candidates.actions';
import { map, mergeMap, of, switchMap } from 'rxjs';
import { catchError, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectStatusSelected } from './candidates.selectors';
import { ProjectsService } from '@app/shared/services/projects.service';
import { getSelectedProjectId } from '../projects/projects.selectors';
import { DrawerService } from '@app/shared/components/right-drawer/drawer.service';
import { Router } from '@angular/router';
import { CANDIDATES_SEARCH_PAGE_PREFIX, NLS_SEARCH_PREFIX } from '@app/app-routing-paths';
import { AgentsService } from '@app/shared/services/agents.service';
import { getChatSessionState } from '@app/collections/chatbot/chatbot.selectors';

@Injectable()
export class CandidateEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private projectsService = inject(ProjectsService);
  private drawerService = inject(DrawerService);
  private agentsService = inject(AgentsService);
  private router = inject(Router);

  loadCandidatesOnStatusChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setStatusId),
      map(() => loadCandidates({ payload: { page: 0 } })),
    ),
  );

  loadCandidates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCandidates),
      withLatestFrom(
        this.store.select(getSelectedProjectId),
        this.store.select(selectStatusSelected),
      ),
      switchMap(([{ payload }, projectId, statusId]) => {
        const serviceCall = this.projectsService.getProjectCandidates(
          statusId,
          projectId,
          payload.page,
        );

        return serviceCall.pipe(
          tap(() => this.drawerService.close()),
          switchMap((candidatesPage) => {
            localStorage.setItem('candidatesPage', JSON.stringify(candidatesPage));
            return [loadCandidatesSuccess({ payload: { candidatesPage } })];
          }),
          catchError((error) => of(loadCandidatesError({ error }))),
        );
      }),
    ),
  );

  loadAiSummaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAiSummaries),
      withLatestFrom(this.store.select(getChatSessionState)),
      mergeMap(([{ payload }, chatState]) =>
        this.agentsService.generateAgentSummaries(chatState.threadId!, [payload.candidateId]).pipe(
          map((response) =>
            loadAiSummariesSuccess({
              payload: {
                summary: {
                  candidateId: payload.candidateId,
                  summary: response.summaries[payload.candidateId] ?? '',
                },
              },
            }),
          ),
          catchError((error) => of(loadAiSummariesError({ error }))),
        ),
      ),
    ),
  );

  navigateAfterSearchSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCandidatesSuccess),
        tap(() => {
          if (this.router.url.includes(NLS_SEARCH_PREFIX)) {
            this.router.navigate([CANDIDATES_SEARCH_PAGE_PREFIX]);
          }
        }),
      ),
    { dispatch: false },
  );
}
